import React from 'react'
import styled from 'styled-components'
import { Tooltip, type TooltipProps } from 'sutra/components/Tooltip/Tooltip'

interface TooltipIconButtonOwnProps {
  tooltipTitle: React.ReactNode
  tooltipSide?: TooltipProps['side']
  tooltipAlign?: TooltipProps['align']
  tooltipZIndex?: number
  iconFontSize?: string
  icon: React.ReactElement
  tooltipOpen?: boolean
  onClick?: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  active?: boolean
  disabled?: boolean
  variant?: 'default' | 'danger' | 'muted'
  height: number
  width: number
  className?: string
  ariaLabel?: string
  'data-chameleon-id'?: string
}

// The rest is forwarded to the button so it can be the trigger of a Sutra
// `DropdownMenu` / `Popover`, which hang their handlers and aria on it.
type TooltipIconButtonProps = TooltipIconButtonOwnProps &
  Omit<
    React.ComponentPropsWithoutRef<'button'>,
    keyof TooltipIconButtonOwnProps | 'children' | 'type'
  >

export const TooltipIconButton = React.forwardRef<
  HTMLButtonElement,
  TooltipIconButtonProps
>(
  (
    {
      tooltipTitle,
      tooltipSide,
      tooltipAlign,
      tooltipZIndex,
      iconFontSize = '16px',
      icon,
      tooltipOpen,
      onClick,
      onMouseEnter,
      onMouseLeave,
      active = false,
      disabled = false,
      variant = 'default',
      height,
      width,
      className,
      ariaLabel,
      'data-chameleon-id': dataChameleonId,
      ...buttonProps
    },
    ref,
  ) => {
    const button = (
      <IconButton
        {...buttonProps}
        ref={ref}
        type="button"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        disabled={disabled}
        $active={active}
        $disabled={disabled}
        $variant={variant}
        $height={height}
        $width={width}
        className={className}
        aria-label={ariaLabel}
        data-chameleon-id={dataChameleonId}
      >
        {React.cloneElement(icon, {
          style: { ...icon.props.style, fontSize: iconFontSize },
        })}
      </IconButton>
    )

    // A native disabled button fires no pointer events, so it cannot be the
    // trigger; antd used to wrap it in a span for the same reason.
    return (
      <Tooltip
        content={tooltipTitle}
        open={tooltipOpen}
        side={tooltipSide}
        align={tooltipAlign}
        zIndex={tooltipZIndex}
      >
        {disabled ? <span>{button}</span> : button}
      </Tooltip>
    )
  },
)

TooltipIconButton.displayName = 'TooltipIconButton'

const IconButton = styled.button<{
  $disabled?: boolean
  $active?: boolean
  $variant?: 'default' | 'danger' | 'muted'
  $height: number
  $width: number
}>`
  border: none;
  padding: 0;
  font: inherit;
  background: ${({ $active, $disabled, $variant }) =>
    $active && !$disabled
      ? $variant === 'muted'
        ? '#eeeff3'
        : '#5072a426'
      : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $width }) => `${$width}px`};
  height: ${({ $height }) => `${$height}px`};
  flex-shrink: 0;
  border-radius: 6px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  color: ${({ $disabled, $variant }) =>
    $disabled
      ? '#c0c0c0'
      : $variant === 'danger'
        ? '#6b7280'
        : $variant === 'muted'
          ? '#7f8695'
          : '#5072a4'};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition:
    background 0.2s ease,
    color 0.2s ease;

  &:hover {
    background: ${({ $disabled, $variant }) =>
      $disabled
        ? 'transparent'
        : $variant === 'danger'
          ? '#fee2e2'
          : $variant === 'muted'
            ? '#eeeff3'
            : '#5072a426'};
    color: ${({ $disabled, $variant }) =>
      $disabled ? '#c0c0c0' : $variant === 'danger' ? '#dc2626' : '#28364f'};
  }
`
